import { ComponentProps, useEffect, useMemo, useState } from 'react';

import { StoreItemOrderForm } from '#components/organisms/forms/StoreItemOrderForm';
import { useRouteStore } from '#hooks/useRouteStore';
import { useShopProvider } from '#provider/useShopProvider.js';
import {
  type StoreItem,
  StoreItemImage,
  StoreItemWithOptionalInventorySummary,
} from '@givebacks/api/store';
import { usePlatformShare } from 'core';
import { ShareModal } from 'core/Components';
import { BASE_URL } from 'core/Utils';
import { Navigate } from 'react-router-dom';
import {
  Box,
  ButtonMantine,
  CarouselWithPreview,
  Container,
  DEFAULT_PTA_IMAGE,
  Group,
  ImageMantine,
} from 'ui';
import { IconShare } from 'ui/Icons';

import classes from './StoreItem.module.css';

const StoreItemCarouselHeader = () => {
  const { useShowStore, itemId } = useRouteStore();
  const { data: cause } = useShowStore();
  const { platformShare } = usePlatformShare();
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const shareLink = useMemo(
    () => `${BASE_URL}/causes/${cause?.uuid}/shop/items/${itemId}`,
    [cause?.uuid, itemId]
  );

  return (
    <>
      <Group w="100%" justify="space-between">
        <ImageMantine src={cause?.thumbnail?.url} w={48} h={48} radius={12} />
        <ButtonMantine
          variant="white"
          color="gray"
          size="sm"
          leftSection={<IconShare />}
          onClick={() =>
            void platformShare(
              {
                share_options: { url: shareLink },
              },
              () => setIsShareModalOpen(true)
            )
          }
        >
          Share
        </ButtonMantine>
      </Group>
      <ShareModal
        opened={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        link={shareLink}
        type="product"
        causeName={cause?.name}
        imgSrc={cause?.logo?.url}
      />
    </>
  );
};

const StoreItemCarousel = ({
  slides,
  isShowPTADefaultImage,
}: {
  slides: string[];
  isShowPTADefaultImage?: boolean;
}) => {
  const _slides = isShowPTADefaultImage ? [DEFAULT_PTA_IMAGE] : slides;
  return <CarouselWithPreview slides={_slides} header={<StoreItemCarouselHeader />} />;
};

const BaseStoreItem = ({ item }: { item: StoreItemWithOptionalInventorySummary }) => {
  const { storeId, useShowStore } = useRouteStore();
  const { data: cause } = useShowStore();

  const isShowPTADefaultImage = useMemo(
    () => cause?.in_state_pta && item?.type === 'StoreItems::Membership' && !item?.images[0],
    [cause?.in_state_pta, item?.images, item?.type]
  );

  const storeItemImages = useMemo(
    () =>
      (item?.images
        ?.map((image?: StoreItemImage) => image?.url)
        .filter((url) => !!url) as string[]) ?? [],
    [item?.images]
  );

  return (
    <Container className={classes.container}>
      <Box className={classes.left_section}>
        <StoreItemCarousel slides={storeItemImages} isShowPTADefaultImage={isShowPTADefaultImage} />
      </Box>
      <Box className={classes.right_section}>
        <StoreItemOrderForm storeItem={item} storeId={storeId} />
      </Box>
    </Container>
  );
};

const StoreItem = () => {
  const { itemId, useShowStoreItem, isCauseLoading } = useRouteStore();
  const { notFoundRedirect } = useShopProvider();

  const { data: storeItem, isLoading } = useShowStoreItem();
  const { action: notFoundRedirectAction, ...notFoundRedirectProps } = useMemo(
    () => notFoundRedirect?.(itemId ?? '') ?? { action: undefined },
    [itemId, notFoundRedirect]
  );

  const willRedirect = useMemo(
    () => !storeItem && !isLoading && !isCauseLoading && !!notFoundRedirect,
    [storeItem, isLoading, isCauseLoading, notFoundRedirect]
  );

  useEffect(() => {
    if (willRedirect) {
      notFoundRedirectAction?.();
    }
  }, [willRedirect, notFoundRedirectAction]);

  if (!storeItem) {
    if (!willRedirect) {
      return null;
    }

    return <Navigate {...(notFoundRedirectProps as ComponentProps<typeof Navigate>)} />;
  }

  return <BaseStoreItem item={storeItem} />;
};

export default StoreItem;
export { BaseStoreItem };
